/**
 * In-memory cache for rendered invoice PDF bytes.
 *
 * Cache key: `${invoice.id}:${invoice.updated_at}:${status}`. The invoice's
 * updated_at is the canonical "this invoice changed" signal — status changes,
 * snapshot writes, and any other meaningful mutation bump it via DB triggers.
 *
 * Cached entries are scoped to the page load (no localStorage / sessionStorage)
 * — a hard reload always re-fetches. The cap below prevents unbounded growth;
 * eviction is simple LRU via insertion-order replay (Map preserves insertion
 * order). Typical PDF: 300KB–1MB → cap=20 ≈ 20MB worst case.
 *
 * Drafts intentionally bypass the cache. The manual-invoice builder mutates
 * draft contents in client state without bumping updated_at, so caching by
 * id+updated_at would serve stale bytes mid-edit. We only cache invoices that
 * are immutable snapshots: sent / paid / voided.
 */

const PDF_CACHE = new Map();
const CACHE_MAX = 20;

/**
 * Compute a cache key for an invoice, or null if it shouldn't be cached.
 */
export function getPdfCacheKey(invoice, styleTag = '') {
  if (!invoice?.id || !invoice?.updated_at) return null;
  const status = (invoice.status || '').toLowerCase();
  if (status !== 'sent' && status !== 'paid' && status !== 'voided') return null;
  // KAN-823: the document style tag (theme + accent) is part of the key, so a
  // style/accent change misses the old render instead of serving it.
  return `${invoice.id}:${invoice.updated_at}:${status}:${styleTag}`;
}

/**
 * Read bytes from the cache, refreshing recency on hit.
 */
export function readPdfCache(key) {
  if (!key || !PDF_CACHE.has(key)) return null;
  const bytes = PDF_CACHE.get(key);
  // Touch: re-insert moves it to "newest" so it survives LRU eviction.
  PDF_CACHE.delete(key);
  PDF_CACHE.set(key, bytes);
  return bytes;
}

/**
 * Write bytes to the cache and evict the oldest entry if we're over the cap.
 */
export function writePdfCache(key, bytes) {
  if (!key || !bytes) return;
  if (PDF_CACHE.has(key)) PDF_CACHE.delete(key);
  PDF_CACHE.set(key, bytes);
  while (PDF_CACHE.size > CACHE_MAX) {
    const oldest = PDF_CACHE.keys().next().value;
    PDF_CACHE.delete(oldest);
  }
}

/**
 * Clear the entire cache. Call on session sign-out (or hard logout) to make
 * sure another user logging into the same tab doesn't see cached bytes for
 * the previous user. Tenant isolation still holds at the edge function (the
 * cache is keyed by invoice id which is unique per tenant), but defense in
 * depth: wipe the in-memory cache too.
 */
export function clearPdfCache() {
  PDF_CACHE.clear();
}
